import { httpsCallable } from 'firebase/functions';

let _functions: ReturnType<typeof import('firebase/functions').getFunctions> | null = null;
async function getFunctionsInstance() {
  if (!_functions) {
    const { getFunctions } = await import('firebase/functions');
    const { app } = await import('./firebase');
    _functions = getFunctions(app);
  }
  return _functions;
}

export interface TeamMemberRow {
  uid: string;
  email: string | null;
  displayName: string | null;
  subscription: string;
  lastActiveAt: number | null;
  isAdmin: boolean;
  uploadCount: number;
  pathCount: number;
  /** Whether this member currently holds a Coach seat (REC-071). */
  coachAssigned: boolean;
}

export interface TeamPendingInvite {
  code: string;
  email: string;
  invitedAt: string;
  expiresAt: string;
}

export interface TeamSummary {
  teamId: string;
  name: string;
  createdAt: string;
  adminUid: string;
  memberCount: number;
  coachSeatPool: {
    purchased: number;
    assigned: number;
    lemonSqueezyVariantId: string;
    updatedAt: string;
  } | null;
  seatPool: { purchased: number; updatedAt: string } | null;
  /** Total Teams licenses incl. founder. */
  seatsPurchased: number;
  /** Seats in use (founder + accepted members). */
  seatsUsed: number;
  /** Outstanding pending invites (each holds a seat). */
  seatsPending: number;
}

export async function createTeam(name: string): Promise<{ teamId: string; created: boolean }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ name: string }, { teamId: string; created: boolean }>(
    functions, 'createTeam',
  );
  const result = await fn({ name });
  return result.data;
}

export async function inviteToTeam(
  teamId: string,
  emails: string[],
): Promise<{ invites: Array<{ email: string; code: string }>; skipped: string[]; seatsRemaining: number }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<
    { teamId: string; emails: string[] },
    { invites: Array<{ email: string; code: string }>; skipped: string[]; seatsRemaining: number }
  >(functions, 'inviteToTeam');
  const result = await fn({ teamId, emails });
  return result.data;
}

export async function listTeamForAdmin(teamId: string): Promise<{
  team: TeamSummary;
  members: TeamMemberRow[];
  pendingInvites: TeamPendingInvite[];
}> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ teamId: string }, {
    team: TeamSummary;
    members: TeamMemberRow[];
    pendingInvites: TeamPendingInvite[];
  }>(functions, 'listTeamForAdmin');
  const result = await fn({ teamId });
  return result.data;
}

export async function revokeTeamInvite(code: string): Promise<void> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ code: string }, { ok: true }>(functions, 'revokeTeamInvite');
  await fn({ code });
}

export interface LeaderboardEntry {
  uid: string;
  displayName: string | null;
  photoURL: string | null;
  totalXp: number;
  weeklyXp: number;
  currentStreakDays: number;
  rank: number;
  rankChange: number;
}

export async function getTeamLeaderboard(teamId: string): Promise<{
  entries: LeaderboardEntry[];
  cohortAverage: number;
}> {
  const { weekStart, weekKey } = currentWeekKeys();
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<
    { teamId: string; weekStart: string; weekKey: string },
    { entries: LeaderboardEntry[]; cohortAverage: number }
  >(functions, 'getTeamLeaderboard');
  const result = await fn({ teamId, weekStart, weekKey });
  return result.data;
}

function currentWeekKeys(): { weekStart: string; weekKey: string } {
  const now = new Date();
  const dow = now.getDay(); // 0 Sun..6 Sat
  // Monday = 1; offset to most recent Monday (Sun is 6 days back).
  const offset = dow === 0 ? 6 : dow - 1;
  const monday = new Date(now);
  monday.setDate(now.getDate() - offset);
  monday.setHours(0, 0, 0, 0);
  const weekStart =
    `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;

  // ISO week number — Thursday-anchored.
  const target = new Date(monday);
  target.setDate(monday.getDate() + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const week = 1 + Math.round(
    ((target.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7,
  );
  const weekKey = `${target.getFullYear()}-W${String(week).padStart(2, '0')}`;
  return { weekStart, weekKey };
}

// ── Coach seats (REC-071) ──────────────────────────────────────────────────

export async function purchaseCoachSeats(
  teamId: string,
  quantity: number,
): Promise<{ ok: true; purchased?: number; checkoutUrl?: string }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<
    { teamId: string; quantity: number },
    { ok: true; purchased?: number; checkoutUrl?: string }
  >(functions, 'purchaseCoachSeats');
  const result = await fn({ teamId, quantity });
  return result.data;
}

export async function assignCoachSeat(
  teamId: string,
  memberUid: string,
): Promise<{ ok: true; alreadyAssigned: boolean }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ teamId: string; memberUid: string }, { ok: true; alreadyAssigned: boolean }>(
    functions, 'assignCoachSeat',
  );
  const result = await fn({ teamId, memberUid });
  return result.data;
}

export async function revokeCoachSeat(
  teamId: string,
  memberUid: string,
): Promise<{ ok: true; alreadyRevoked: boolean }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ teamId: string; memberUid: string }, { ok: true; alreadyRevoked: boolean }>(
    functions, 'revokeCoachSeat',
  );
  const result = await fn({ teamId, memberUid });
  return result.data;
}

export async function removeTeamMember(teamId: string, memberUid: string): Promise<void> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ teamId: string; memberUid: string }, { ok: true }>(
    functions, 'removeTeamMember',
  );
  await fn({ teamId, memberUid });
}

export async function claimPendingTeamInvites(): Promise<{ claimed: Array<{ teamId: string }> }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<Record<string, never>, { ok: true; claimed: Array<{ teamId: string }> }>(
    functions, 'claimPendingTeamInvites',
  );
  const result = await fn({});
  return result.data;
}
